import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
import { experienceGroups } from '../data/profile';
import type { ExperienceEntry, ExperienceGroup } from '../types/profile';
const EASE = [0.23, 1, 0.32, 1] as const;
interface ExperienceSectionProps {
  layout?: 'tabs' | 'accordion';
}
export function ExperienceSection({
  layout = 'tabs'
}: ExperienceSectionProps) {
  return <section className="bg-white py-16 lg:py-24" data-id="element-54" data-mp="1b125i46vy1sr">
      <div className="mx-auto max-w-shell px-6 lg:px-8" data-id="element-55" data-mp="1f2drlzqjw4w7">
        <div data-id="element-56" data-mp="1f3ygp9u3ivmo">
          {layout === 'tabs' ? <TabsView data-id="element-57" data-mp="11az6nnd31tmi" /> : <AccordionView data-id="element-58" data-mp="11b8t50yx5ing" />}
        </div>
      </div>
    </section>;
}
function TabsView() {
  const [active, setActive] = useState(experienceGroups[0].id);
  const group = experienceGroups.find(g => g.id === active) ?? experienceGroups[0];
  return <div className="grid gap-10 lg:grid-cols-12 lg:gap-14" data-id="element-59" data-mp="1txa78y0ovujb">
      <div className="lg:col-span-4" data-id="element-60" data-mp="1yrec9bqe9shr">
        <div role="tablist" aria-label="Experience categories" className="flex gap-2 overflow-x-auto border-b border-mist pb-px lg:flex-col lg:gap-0 lg:overflow-visible lg:border-b-0 lg:border-l lg:border-mist lg:pb-0" data-id="element-61" data-mp="2fabhizhpsooj">
          {experienceGroups.map(g => {
          const selected = g.id === active;
          return <button key={g.id} role="tab" id={`tab-${g.id}`} aria-selected={selected} aria-controls={`panel-${g.id}`} onClick={() => setActive(g.id)} className={`relative whitespace-nowrap px-4 py-3 text-left text-sm font-semibold transition-colors duration-150 ease-out lg:whitespace-normal lg:px-6 lg:py-4 ${selected ? 'text-navy' : 'text-slate-500 hover:text-navy'}`} data-id="element-62" data-mp="2k83jynfzjt6z">
                {selected && <motion.span layoutId="tab-marker" transition={{
              duration: 0.25,
              ease: EASE
            }} className="absolute bottom-0 left-0 h-0.5 w-full bg-gold lg:bottom-auto lg:left-[-1px] lg:top-0 lg:h-full lg:w-0.5" data-id="element-63" data-mp="18nzjkjd1xyh0" />}
                {g.label}
              </button>;
        })}
        </div>
      </div>

      <div className="lg:col-span-8" data-id="element-64" data-mp="3mlkn4b2eplfa">
        <AnimatePresence mode="wait" data-id="element-65" data-mp="3rns5pjhqrzql">
          <motion.div key={group.id} id={`panel-${group.id}`} role="tabpanel" aria-labelledby={`tab-${group.id}`} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -6
        }} transition={{
          duration: 0.22,
          ease: EASE
        }} data-id="element-66" data-mp="3rmqhedbptgqx">
            <h2 className="font-serif text-xl leading-snug text-navy" data-id="element-67" data-mp="e8vh1bc4w1xc">
              {group.headline}
            </h2>
            <div className="mt-8 flex flex-col" data-id="element-68" data-mp="j7yof2fl6k8h">
              {group.entries.map(entry => <EntryRow key={entry.role} entry={entry} data-id="element-69" data-mp="23q0sqxleeuwc" />)}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
}
function AccordionView() {
  const [open, setOpen] = useState<string | null>(experienceGroups[0].id);
  return <div className="border-t border-mist" data-id="element-70" data-mp="1pq85xdczsytn">
      {experienceGroups.map((group: ExperienceGroup) => {
      const expanded = open === group.id;
      return <div key={group.id} className="border-b border-mist" data-id="element-71" data-mp="2mhju6zzry0w9">
            <h3 data-id="element-72" data-mp="2ienk5xoavd3x">
              <button type="button" aria-expanded={expanded} aria-controls={`acc-${group.id}`} onClick={() => setOpen(expanded ? null : group.id)} className="flex w-full items-center justify-between gap-6 py-6 text-left" data-id="element-73" data-mp="2ig8d5iuyunzo">
                <span className="font-serif text-xl text-navy" data-id="element-74" data-mp="31cztpkj4ip01">{group.label}</span>
                <ChevronDownIcon className={`h-5 w-5 shrink-0 text-gold-deep transition-transform duration-200 ease-out ${expanded ? 'rotate-180' : ''}`} data-id="element-75" data-mp="31awv4c3wnnjp" />
              </button>
            </h3>
            <AnimatePresence initial={false} data-id="element-76" data-mp="2by3uq0eew93l">
              {expanded && <motion.div id={`acc-${group.id}`} initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.25,
            ease: EASE
          }} className="overflow-hidden" data-id="element-77" data-mp="2gv3rfkpdfzi1">
                  <div className="pb-8" data-id="element-78" data-mp="2utty9s1ztdjl">
                    <p className="max-w-3xl text-base text-charcoal" data-id="element-79" data-mp="2zty5erh0gvx5">
                      {group.headline}
                    </p>
                    <div className="mt-6 flex flex-col" data-id="element-80" data-mp="3nj0wsr1g634u">
                      {group.entries.map(entry => <EntryRow key={entry.role} entry={entry} data-id="element-81" data-mp="3sd563gy6qn93" />)}
                    </div>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>;
    })}
    </div>;
}
function EntryRow({
  entry
}: {
  entry: ExperienceEntry;
}) {
  return <article className="border-t border-mist py-7 first:border-t-0 first:pt-0" data-id="element-82" data-mp="abgd48kxbddw">
      <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1" data-id="element-83" data-mp="adj1ko6fgygi">
        <h3 className="text-base font-semibold text-navy" data-id="element-84" data-mp="3it2q59w5lsp8">{entry.organization}</h3>
        <span className="text-[11px] font-semibold uppercase tracking-overline text-slate-400" data-id="element-85" data-mp="3eqzf2mdecq4h">
          {entry.period}
        </span>
      </div>
      <p className="mt-1 font-serif text-lg leading-snug text-gold-deep" data-id="element-86" data-mp="3nqclt7a4z0li">
        {entry.role}
      </p>
      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-charcoal" data-id="element-87" data-mp="17yfbcnr9ktdw">
        {entry.summary}
      </p>
      {entry.metrics && <ul className="mt-4 flex flex-wrap gap-2" data-id="element-88" data-mp="12zc6b3baan5x">
          {entry.metrics.map(metric => <li key={metric} className="bg-offwhite px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-89" data-mp="1azrzpn2qh3v2">
              {metric}
            </li>)}
        </ul>}
    </article>;
}